import { SignalsServiceDefinition } from './core-services/signals/definition.js';
import { SignalsServiceFactory } from './core-services/signals/service.js';
import { createSignalsRegistry, SignalsRegistry, } from './core-services/signals/registry.js';
import { wixContext } from '@wix/sdk-context';
export { createSignalsRegistry, SignalsRegistry };
export function createServicesManager(servicesBindings, { 
// in case we decide to keep multiple services manager in the same thread, we can use one signals registry for all of them
signalsRegistry = createSignalsRegistry(), } = {}) {
    const coreRegisteredServices = createServicesMap().addService(SignalsServiceDefinition, SignalsServiceFactory, { signalsRegistry });
    const bindingsByDefinition = new Map([
        ...(servicesBindings ? servicesBindings.registeredServices : []),
        ...coreRegisteredServices.registeredServices,
    ].map((service) => [service.definition.toString(), service]));
    const initializedServices = new Map();
    const manager = {
        getSignalsRegistry() {
            return signalsRegistry;
        },
        getServiceSignals(definition) {
            if (!manager.hasService(definition)) {
                throw new Error(`Service ${definition.toString()} is defined in services manager`);
            }
            return signalsRegistry.getServiceSignals(definition.toString());
        },
        getService(definition, getService = manager.getService, signals = {}) {
            if (!initializedServices.has(definition.toString())) {
                const service = bindingsByDefinition.get(definition.toString());
                if (!service) {
                    // this needs meaningful error message
                    throw new Error(`Service ${definition} is not provided`);
                }
                initializedServices.set(definition.toString(), service.impl({
                    config: { ...service.config, signals },
                    getService,
                }));
            }
            return initializedServices.get(definition.toString());
        },
        hasService(definition) {
            return bindingsByDefinition.has(definition.toString());
        },
        addService(definition, impl, config) {
            if (!manager.hasService(definition)) {
                bindingsByDefinition.set(definition.toString(), {
                    definition,
                    impl,
                    config: config || {},
                });
                // Immediately initialize the service if not already initialized
                if (!initializedServices.has(definition.toString())) {
                    initializedServices.set(definition.toString(), impl({
                        config: config || {},
                        getService: manager.getService,
                    }));
                }
            }
            else {
                // this needs meaningful error message
                throw new Error(`Service ${definition.toString()} is already provided`);
            }
        },
        addServices(servicesToAdd) {
            // First, check for duplicates
            servicesToAdd.registeredServices.forEach(({ definition }) => {
                if (manager.hasService(definition)) {
                    throw new Error(`Service ${definition.toString()} is already provided`);
                }
            });
            // Register all definitions first
            servicesToAdd.registeredServices.forEach(({ definition, impl, config }) => {
                bindingsByDefinition.set(definition.toString(), {
                    definition,
                    impl,
                    config: config || {},
                });
            });
            // Then initialize all, but only if not already initialized
            servicesToAdd.registeredServices.forEach(({ definition, impl, config }) => {
                if (!initializedServices.has(definition.toString())) {
                    initializedServices.set(definition.toString(), impl({
                        config: config || {},
                        getService: manager.getService,
                    }));
                }
            });
        },
        enableContext(context) {
            let resolvedContext = wixContext;
            if (context === 'global') {
                if (!globalThis.__wix_context__) {
                    globalThis.__wix_context__ = {};
                }
                resolvedContext = globalThis.__wix_context__;
            }
            resolvedContext.getService = this.getService;
            resolvedContext.awaitRemoteSignals = async () => 
            // remote services manager
            this.awaitConnectionFromMain
                ? this.awaitConnectionFromMain()
                : true;
            return this;
        },
    };
    // Then initialize all, but only if not already initialized
    bindingsByDefinition.forEach(({ definition, impl, config }) => {
        if (!initializedServices.has(definition.toString())) {
            initializedServices.set(definition, impl({
                config: config || {},
                getService: manager.getService,
            }));
        }
    });
    return manager;
}
export function createServicesMap(registeredServices = []) {
    return {
        registeredServices,
        addService(definition, impl, config) {
            return createServicesMap([
                ...registeredServices,
                { definition, impl, config },
            ]);
        },
    };
}
